import React, { useEffect, useState } from 'react'
import { Link, useForm, usePage } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import AlertDismissible from '../../Shared/Components/AlertDismissible'
import Breadcrumbs from '../../Shared/Components/Breadcrumbs'
import BasicRichTextEditor from '../../Shared/Components/BasicRichTextEditor'
import FormValidationSummary, {
    mergeValidationErrors,
    validationMessage,
} from '../../Shared/Components/FormValidationSummary'
import Layout from '../../Shared/Layout/LayoutAdmin'
import { aboutUsSectionUrl } from '../../utils/aboutUsPermissions'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Nav from 'react-bootstrap/Nav'
import Row from 'react-bootstrap/Row'
import Tab from 'react-bootstrap/Tab'
import './about-us.css'

function SectionForm({ section, pageErrors }) {
    const [preview, setPreview] = useState(null)
    const [fileInputKey, setFileInputKey] = useState(0)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)

    const form = useForm({
        content: section.content || '',
        photo: null,
    })

    useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview])

    const allErrors = mergeValidationErrors(form.errors, pageErrors)
    const fieldError = (key) => validationMessage(allErrors, key)

    const onPhotoChange = (e) => {
        const file = e.target.files?.[0] || null
        form.setData('photo', file)
        setPreview(file ? URL.createObjectURL(file) : null)
    }

    const clearSelectedPhoto = () => {
        form.setData('photo', null)
        setPreview(null)
        setFileInputKey((k) => k + 1)
    }

    const submit = (e) => {
        e.preventDefault()
        form.post(route('about-us.manage.update', section.slug), {
            forceFormData: true,
            preserveScroll: true,
            onSuccess: clearSelectedPhoto,
        })
    }

    const destroyPhoto = () => {
        setDeleting(true)
        Inertia.delete(route('about-us.manage.photo.destroy', section.slug), {
            preserveScroll: true,
            onFinish: () => {
                setDeleting(false)
                setConfirmDelete(false)
            },
        })
    }

    const shownPhoto = preview || section.photo_url

    return (
        <Form onSubmit={submit}>
            <Row className="g-8">
                <Col lg={4}>
                    <Form.Label className="fw-bold">Fotografía</Form.Label>
                    {shownPhoto ? (
                        <img src={shownPhoto} alt={section.title} className="about-us-manage-photo mb-4" />
                    ) : (
                        <div className="about-us-manage-photo about-us-photo--empty d-flex align-items-center justify-content-center mb-4" style={{ height: 200 }}>
                            <span className="text-gray-500">Sin fotografía</span>
                        </div>
                    )}
                    {preview && (
                        <div className="text-muted fs-7 mb-3">Vista previa: se guardará al presionar «Guardar».</div>
                    )}
                    <Form.Control
                        key={fileInputKey}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={onPhotoChange}
                        className={fieldError('photo') ? 'is-invalid' : ''}
                    />
                    {fieldError('photo') && <div className="invalid-feedback d-block">{fieldError('photo')}</div>}
                    <div className="form-text">JPG, PNG o WEBP, máximo 5 MB. Se redimensiona a 1600 px de ancho.</div>
                    <div className="d-flex gap-2 mt-4">
                        {preview && (
                            <Button variant="light" size="sm" onClick={clearSelectedPhoto}>
                                Descartar selección
                            </Button>
                        )}
                        {section.photo_url && !preview && (
                            <Button variant="light-danger" size="sm" onClick={() => setConfirmDelete(true)}>
                                <i className="bi bi-trash me-1"></i> Eliminar fotografía
                            </Button>
                        )}
                    </div>
                </Col>
                <Col lg={8}>
                    <Form.Label className="fw-bold">Texto</Form.Label>
                    <BasicRichTextEditor
                        value={form.data.content}
                        onChange={(html) => form.setData('content', html)}
                        placeholder="Escribe el contenido de la sección…"
                        isInvalid={Boolean(fieldError('content'))}
                    />
                    {fieldError('content') && <div className="invalid-feedback d-block">{fieldError('content')}</div>}
                </Col>
            </Row>

            <div className="d-flex gap-3 mt-8">
                <Button type="submit" variant="primary" disabled={form.processing}>
                    {form.processing ? 'Guardando…' : 'Guardar'}
                </Button>
                <Link href={aboutUsSectionUrl(section.slug)} className="btn btn-light">
                    Ver sección
                </Link>
            </div>

            {confirmDelete && (
                <Modal show onHide={() => !deleting && setConfirmDelete(false)} centered>
                    <Modal.Header closeButton={!deleting}>
                        <Modal.Title>Eliminar fotografía</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        ¿Eliminar la fotografía de <strong>{section.title}</strong>? Esta acción no se puede deshacer.
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="light" disabled={deleting} onClick={() => setConfirmDelete(false)}>
                            Cancelar
                        </Button>
                        <Button variant="danger" disabled={deleting} onClick={destroyPhoto}>
                            {deleting ? 'Eliminando…' : 'Eliminar'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Form>
    )
}

const Manage = ({ sections, activeTab }) => {
    const { flash, errors: pageErrors } = usePage().props
    const [tab, setTab] = useState(activeTab)

    useEffect(() => setTab(activeTab), [activeTab])

    return (
        <Row>
            {flash?.success && <AlertDismissible message={flash.success} type="success" />}
            {flash?.error && <AlertDismissible message={flash.error} type="danger" />}
            <FormValidationSummary errors={pageErrors} />
            <span className="pb-7 pt-7">
                <Breadcrumbs currentPath="admin/quienes-somos/administrar" />
            </span>

            <Card className="card-flush shadow-sm mb-10">
                <Card.Header className="pt-7">
                    <Card.Title className="fw-bold text-gray-800">Administrar Quiénes somos</Card.Title>
                </Card.Header>
                <Card.Body className="py-8 px-6 px-lg-10">
                    <Tab.Container activeKey={tab} onSelect={(k) => k && setTab(k)}>
                        <Nav variant="tabs" className="mb-8 flex-wrap">
                            {sections.map((section) => (
                                <Nav.Item key={section.slug}>
                                    <Nav.Link eventKey={section.slug}>{section.title}</Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                        <Tab.Content>
                            {sections.map((section) => (
                                <Tab.Pane eventKey={section.slug} key={section.slug}>
                                    <SectionForm section={section} pageErrors={tab === section.slug ? pageErrors : {}} />
                                </Tab.Pane>
                            ))}
                        </Tab.Content>
                    </Tab.Container>
                </Card.Body>
            </Card>
        </Row>
    )
}

Manage.layout = (page) => <Layout title="Administrar Quiénes somos">{page}</Layout>

export default Manage
